import { useAuth0 } from '@auth0/auth0-react';
import { RequestActionType } from '@strata/react-chat';
import { DialogWithForm } from '@strata/react-dialog';
import { SuggestSingleSelectFormik } from '@strata/react-selects';
import { SuccessToast } from '@strata/react-toast-message';
import { Formik } from 'formik';
import * as Yup from 'yup';

import { handleError } from 'services/Error';
import { type PayerRequestActionDialogProps } from 'services/interfaces';
import {
    useGetCompassUserNameMapQuery,
    useGetPayerUserWithPermNameMapQuery,
} from 'store/api/authApi';
import { usePostRequestActivityMutation } from 'store/api/payerRequestApi';

const Title = 'Select/Change Assignment';

/** UPN value for unassigned since undefined would break the required validation */
const UNASSIGNED = 'Unassigned';

const getUndefinedForUnassigned = (value: string | null | undefined) =>
    value == null || value === UNASSIGNED ? undefined : value;

export const UpdateAssignmentDialog = ({
    onClose,
    payerId,
    requestDetails,
}: PayerRequestActionDialogProps) => {
    const { user } = useAuth0();
    const [postRequestActivity] = usePostRequestActivityMutation();
    const { data: compassUsers, isFetching: compassUsersFetching } =
        useGetCompassUserNameMapQuery();
    const requestType = requestDetails?.requestType.toString().toLowerCase();
    const { pbmOnly: requestIsPBMOnly } = requestDetails;
    const { data: clientUsers, isFetching: clientUsersFetching } =
        useGetPayerUserWithPermNameMapQuery({
            segmentId: requestDetails.segmentId,
            permission: `write:payer_request_${requestType}`,
        });

    const internalUsers = [
        { value: UNASSIGNED, name: UNASSIGNED },
        { value: user?.email, name: 'Assign to Me' },
        ...Object.entries({ ...compassUsers })
            .filter(([upn]) => upn !== user?.email)
            .map(([upn, name]) => ({ value: upn, name })),
    ];

    const externalUsers = [
        { value: UNASSIGNED, name: UNASSIGNED, hasPBMOnlyAccess: true },
        ...Object.entries({ ...clientUsers })
            .filter(([upn]) => !upn.endsWith('@freemarkethealth.com'))
            .map(([upn, { full_name: fullName, has_pbm_only_access: hasPBMOnlyAccess }]) => ({
                value: upn,
                name: fullName,
                hasPBMOnlyAccess,
            })),
    ];

    return (
        <Formik
            initialValues={{
                assignment: requestDetails.assignedUpn ?? UNASSIGNED,
                payerAssignment: requestDetails.payerAssignedUpn ?? UNASSIGNED,
            }}
            enableReinitialize
            validateOnMount
            validationSchema={Yup.object({
                assignment: Yup.string(),
                payerAssignment: Yup.string(),
            })}
            onSubmit={async (values, { resetForm }) => {
                const assignment = getUndefinedForUnassigned(values.assignment);
                const payerAssignment = getUndefinedForUnassigned(values.payerAssignment);
                const payerAssignmentChanged =
                    getUndefinedForUnassigned(requestDetails.payerAssignedUpn) !== payerAssignment;
                try {
                    if (getUndefinedForUnassigned(requestDetails.assignedUpn) !== assignment) {
                        await postRequestActivity({
                            payerId,
                            requestId: requestDetails.requestId,
                            actionType: RequestActionType.ASSIGN_UP,
                            actionDetail: assignment,
                            // Only invalidate tags if the payer assignment won't already take care of it
                            skipTagInvalidation: payerAssignmentChanged,
                        }).unwrap();
                    }
                    if (payerAssignmentChanged) {
                        await postRequestActivity({
                            payerId,
                            requestId: requestDetails.requestId,
                            actionType: RequestActionType.ASSIGN_CL,
                            actionDetail: payerAssignment,
                        }).unwrap();
                    }
                    SuccessToast({ message: 'Assignment updated successfully.' });
                    resetForm();
                    onClose();
                } catch (err) {
                    handleError('Unable to update assignment. Please try again.', err as Error);
                }
            }}
        >
            {(formik) => (
                <DialogWithForm
                    dialogTitle={Title}
                    width={600}
                    isOpen
                    secondaryButtonLabel="Cancel"
                    onSecondaryButtonClick={() => {
                        onClose();
                        formik.resetForm();
                    }}
                    primaryButtonLabel="Assign Request"
                    isPrimaryButtonDisabled={
                        !(formik.isValid && formik.dirty) || formik.isSubmitting
                    }
                    dialogContent={
                        <>
                            <SuggestSingleSelectFormik
                                id="assignment"
                                label="FMH Assignee"
                                isLoading={compassUsersFetching}
                                values={internalUsers}
                                selectedItem={internalUsers?.find(
                                    ({ value }) => value === formik.values.assignment,
                                )}
                                onChange={(newVal) => {
                                    void formik.setFieldValue('assignment', newVal?.value);
                                }}
                            />
                            <SuggestSingleSelectFormik
                                id="payerAssignment"
                                label="Payer Assignee"
                                isLoading={clientUsersFetching}
                                values={externalUsers.filter((option) =>
                                    requestIsPBMOnly === true ? option?.hasPBMOnlyAccess : true,
                                )}
                                selectedItem={externalUsers?.find(
                                    ({ value }) => value === formik.values.payerAssignment,
                                )}
                                onChange={(newVal) => {
                                    void formik.setFieldValue('payerAssignment', newVal?.value);
                                }}
                            />
                        </>
                    }
                />
            )}
        </Formik>
    );
};

UpdateAssignmentDialog.Title = Title;
